import { useQuery } from "@rocicorp/zero/react"
import {
	IconCalendarOutline18 as Calendar,
	IconCheckOutline18 as Check,
} from "nucleo-ui-outline-18"
import Markdown from "react-markdown"

import {
	Avatar,
	Badge,
	CircularProgress,
	IssueStatusIcon,
	ProjectStatusIcon,
} from "@productlane/ui"
import { CommandItem } from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { IssueSelectIssue, ProjectSelectProject } from "./issue-select"
import type { ProjectPreviewInput } from "./shared/project-preview"
import {
	HoverCard,
	HoverCardContent,
	HoverCardPortal,
	HoverCardTrigger,
} from "@/components/hover-card"
import { api } from "@/lib/api"
import { getProjectIcon } from "@/lib/projects"
import { IssuePriorityIcon } from "@/screens/app/issues/issue-priority-icon"
import { resolveLinearPerson } from "./shared/linear-person"
import { resolveLinearTeam } from "./shared/linear-team"
import { useRowPreview } from "./shared/preview-suppression"
import { getProjectPreviewDetails } from "./shared/project-preview"

const LinearTeamLabel = ({
	teamId,
	fallbackName,
	extraCount = 0,
}: {
	teamId: string | null | undefined
	fallbackName?: string | null
	extraCount?: number
}) => {
	const { data: teams } = api.linear.teams.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const team = resolveLinearTeam(teamId, teams, fallbackName)
	if (!team) return null
	const Icon = getProjectIcon(team.icon)
	return (
		<div className="flex min-w-0 items-center gap-1.5">
			<Icon
				className="h-3.5 w-3.5 flex-none"
				style={{ color: team.color ?? undefined }}
			/>
			<span className="truncate">{team.name}</span>
			{extraCount > 0 && <span className="text-secondary">+{extraCount}</span>}
		</div>
	)
}

const PersonLabel = ({
	name,
	avatarUrl,
}: {
	name: string
	avatarUrl: string | null
}) => (
	<div className="flex min-w-0 items-center gap-1.5">
		<Avatar name={name} image={avatarUrl} size="xxs" className="flex-none" />
		<span className="truncate">{name}</span>
	</div>
)

const SyncedLinearPersonLabel = ({
	linearUserId,
}: {
	linearUserId: string
}) => {
	const [users] = useQuery(queries.linearUsers())
	const person = resolveLinearPerson(linearUserId, users)
	return person ? <PersonLabel {...person} /> : null
}

// Only subscribes to the Linear users when the row has nobody inline.
const LinearPersonLabel = ({
	linearUserId,
	person,
}: {
	linearUserId: string | null | undefined
	person?: { name?: string | null; avatarUrl?: string | null } | null
}) => {
	const inline = resolveLinearPerson(linearUserId, null, person)
	if (inline) return <PersonLabel {...inline} />
	if (!linearUserId) return null
	return <SyncedLinearPersonLabel linearUserId={linearUserId} />
}

const KindLabel = ({ children }: { children: string }) => (
	<span className="flex-none font-normal text-tertiary">{children}</span>
)

export const IssueListItem = ({
	issue,
	onSelect,
	issueIds,
	oneLine = false,
}: {
	issue: IssueSelectIssue
	onSelect: () => void
	issueIds: Array<string>
	/** Identifier and title on one row, with an Issue label on the right. */
	oneLine?: boolean
}) => {
	const { onPointerMove, ...hoverCard } = useRowPreview(
		`issue:${issue.linearTicketId}`,
	)
	return (
		<HoverCard
			key={issue.identifier}
			openDelay={0}
			closeDelay={0}
			{...hoverCard}
		>
			<HoverCardTrigger onPointerMove={onPointerMove}>
				<CommandItem onSelect={onSelect} value={issue.identifier}>
					<div className="flex items-center gap-3">
						<div className="ml-0.5 h-3.5 w-3.5">
							<IssueStatusIcon
								className="h-3.5 w-3.5"
								color={issue.statusColor ?? undefined}
								status={issue.status ?? undefined}
							/>
						</div>
						{oneLine ? (
							<div className="flex min-w-0 flex-1 items-baseline gap-2">
								<span className="flex-none font-normal tabular-nums text-tertiary">
									{issue.identifier}
								</span>
								<span className="truncate">{issue.title}</span>
							</div>
						) : (
							<div className="flex-1 truncate">
								<div className="truncate text-xs font-normal text-secondary">
									{issue.identifier}
								</div>
								<div className="truncate font-medium">{issue.title}</div>
							</div>
						)}
						{oneLine && <KindLabel>Issue</KindLabel>}
						{issueIds.includes(issue.linearTicketId) && (
							<Check className="h-4 w-4 flex-none" />
						)}
					</div>
				</CommandItem>
			</HoverCardTrigger>

			<HoverCardPortal>
				<HoverCardContent
					className="w-[32rem]"
					sideOffset={10}
					align="start"
					side="left"
				>
					<IssuePreviewCard issue={issue} />
				</HoverCardContent>
			</HoverCardPortal>
		</HoverCard>
	)
}

export const IssuePreviewCard = ({ issue }: { issue: IssueSelectIssue }) => (
	<div className="flex max-h-[32rem] flex-col gap-2">
		<div className="text-sm font-normal text-secondary">{issue.identifier}</div>

		<div className="font-medium" style={{ overflowWrap: "anywhere" }}>
			{issue.title}
		</div>

		<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-normal text-secondary">
			<div className="flex items-center gap-1.5">
				<IssuePriorityIcon
					priority={issue.priority}
					className="h-3.5 w-3.5 flex-none text-secondary"
				/>
				<span>{issue.priority}</span>
			</div>
			<div className="flex items-center gap-1.5">
				<IssueStatusIcon
					className="h-3.5 w-3.5 flex-none"
					color={issue.statusColor ?? undefined}
					status={issue.status ?? undefined}
				/>
				{issue?.statusName}
			</div>
			<LinearPersonLabel
				linearUserId={issue.assigneeId}
				person={issue.assignee}
			/>
			<LinearTeamLabel teamId={issue.team} />
		</div>

		{issue.description && (
			<Markdown className="prose prose-sm mt-1 min-h-0 max-w-none overflow-y-auto border-t border-gray-200 pb-2 pt-3 text-secondary dark:prose-invert prose-headings:mb-1 prose-headings:mt-3 prose-headings:text-sm prose-headings:font-semibold prose-headings:text-primary prose-p:my-1 prose-ul:my-1 prose-li:my-0 dark:border-gray-700/80">
				{issue.description}
			</Markdown>
		)}
	</div>
)

export const ProjectPreviewCard = ({
	project,
}: {
	project: ProjectSelectProject & ProjectPreviewInput
}) => {
	const Icon = getProjectIcon(project.icon)
	const details = getProjectPreviewDetails(project)
	return (
		<div className="flex max-h-[32rem] flex-col gap-2">
			<div
				className="flex items-center gap-2 font-medium"
				style={{ overflowWrap: "anywhere" }}
			>
				<Icon
					className="h-4 w-4 flex-none"
					style={{ color: project.color ?? "" }}
				/>
				{project.name}
			</div>

			{project.description && (
				<Markdown className="prose line-clamp-4 overflow-y-auto text-sm text-secondary dark:prose-invert">
					{project.description}
				</Markdown>
			)}

			<div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-200 pt-3 text-sm text-primary dark:border-gray-700/80">
				{details.statusLabel && (
					<div className="flex items-center gap-1.5">
						{details.status && (
							<ProjectStatusIcon
								className="h-3.5 w-3.5"
								status={details.status}
								customColor={details.statusColor ?? undefined}
								colored
							/>
						)}
						{details.statusLabel}
					</div>
				)}
				<LinearPersonLabel linearUserId={project.leadId} />
				<LinearTeamLabel
					teamId={details.primaryTeamId}
					fallbackName={details.primaryTeam}
					extraCount={details.extraTeamCount}
				/>
				{details.dateLabel && (
					<div className="flex items-center gap-1.5">
						<Calendar className="h-3.5 w-3.5 flex-none text-secondary" />
						{details.dateLabel}
					</div>
				)}
				{details.progressPercent !== null && (
					<div className="flex items-center gap-1.5">
						<CircularProgress
							percent={details.progressPercent}
							className="h-3.5 w-3.5 flex-none"
							thickness={14}
						/>
						{details.progressPercent}%
					</div>
				)}
			</div>
		</div>
	)
}

export const ProjectListItem = ({
	project,
	onSelect,
	projectIds,
	value = `project-${project.id}`,
	oneLine = false,
}: {
	project: ProjectSelectProject & ProjectPreviewInput
	onSelect: () => void
	projectIds: Array<string>
	value?: string
	/** Matches the one-line issue rows: a Project label on the right. */
	oneLine?: boolean
}) => {
	const Icon = getProjectIcon(project.icon)
	const { onPointerMove, ...hoverCard } = useRowPreview(`project:${project.id}`)
	return (
		<HoverCard key={project.id} openDelay={0} closeDelay={0} {...hoverCard}>
			<HoverCardTrigger onPointerMove={onPointerMove}>
				<CommandItem value={value} onSelect={onSelect}>
					<div className="flex items-center gap-2">
						<Icon
							className="mr-1 h-4 w-4 flex-none"
							style={{ color: project.color ?? "" }}
						/>
						<span className="flex-1 truncate">{project.name}</span>
						{project.state === "completed" && (
							<Badge size="sm">
								<ProjectStatusIcon
									className="h-3 w-3"
									status="completed"
									colored
								/>
								Completed
							</Badge>
						)}
						{oneLine && <KindLabel>Project</KindLabel>}
						{projectIds.includes(project.id) && (
							<Check className="h-4 w-4 flex-none" />
						)}
					</div>
				</CommandItem>
			</HoverCardTrigger>
			<HoverCardPortal>
				<HoverCardContent
					className="w-[26rem]"
					sideOffset={10}
					align="start"
					side="left"
				>
					<ProjectPreviewCard project={project} />
				</HoverCardContent>
			</HoverCardPortal>
		</HoverCard>
	)
}
